import { Eye, Download, Trash2, Image, Video, FileText } from 'lucide-react';
import { FileMeta } from '@/types/files';

interface FileCardProps {
  file: FileMeta;
  onPreview: () => void;
  onDownload: () => void;
  onDelete: () => void;
  size?: 'sm' | 'md';
  showDelete?: boolean;
}

export const FileCard: React.FC<FileCardProps> = ({
  file,
  onPreview,
  onDownload,
  onDelete,
  size = 'md',
  showDelete = true,
}) => {
  const getFileIcon = () => {
    if (file.content_type?.startsWith('image/')) {
      return <Image className="h-5 w-5 text-blue-500" />;
    } else if (file.content_type?.startsWith('video/')) {
      return <Video className="h-5 w-5 text-purple-500" />;
    } else {
      return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const cardSize = size === 'sm' ? 'p-2' : 'p-3';
  const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';
  const dateSize = size === 'sm' ? 'text-[10px]' : 'text-xs';

  return (
    <div
      onClick={onPreview}
      className={`
        group relative bg-white border border-gray-200 rounded-lg 
        ${cardSize} 
        hover:border-blue-300 hover:shadow-md 
        transition-all duration-200 
        cursor-pointer
      `}
    >
      {/* File Info */}
      <div className="flex items-start gap-2.5 mb-3">
        <div className="flex-shrink-0 mt-0.5">
          {getFileIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <p 
            className={`${textSize} font-medium text-gray-900 truncate`} 
            title={file.file_name}
          >
            {file.file_name}
          </p>
          <p className={`${dateSize} text-gray-500 mt-0.5`}>
            {new Date(file.upload_date).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100">
        {/* Preview Button */}
        <div className="relative group/btn">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview();
            }}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200 hover:scale-110"
            aria-label="Preview"
          >
            <Eye className={iconSize} />
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
            Preview
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>

        {/* Download Button */}
        <div className="relative group/btn">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDownload();
            }}
            className="p-1.5 text-gray-600 hover:bg-gray-50 rounded-md transition-all duration-200 hover:scale-110"
            aria-label="Download"
          >
            <Download className={iconSize} />
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
            Download
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>

        {/* Delete Button - Only show if showDelete is true */}
        {showDelete && (
          <div className="relative group/btn ml-auto">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-all duration-200 hover:scale-110"
              aria-label="Delete"
            >
              <Trash2 className={iconSize} />
            </button>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
              Delete
              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

